import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  HomeIcon,
  Square3Stack3DIcon,
  PlusIcon,
  BuildingStorefrontIcon,
  PrinterIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  Square3Stack3DIcon as Square3Stack3DIconSolid,
  PlusIcon as PlusIconSolid,
  BuildingStorefrontIcon as BuildingStorefrontIconSolid,
  PrinterIcon as PrinterIconSolid,
  SparklesIcon as SparklesIconSolid,
} from '@heroicons/react/24/solid';

interface NavItemProps {
  icon: React.ComponentType<{ className?: string }>;
  activeIcon?: React.ComponentType<{ className?: string }>;
  label: string;
  href?: string;
  onClick?: () => void;
  primary?: boolean;
  isActive?: boolean;
  badge?: number | boolean;
}

function NavItem({
  icon: Icon,
  activeIcon: ActiveIcon,
  label,
  href,
  onClick,
  primary,
  isActive,
  badge,
}: NavItemProps) {
  const CurrentIcon = isActive && ActiveIcon ? ActiveIcon : Icon;

  const content = (
    <div
      className={cn(
        'flex flex-col items-center justify-center min-h-[56px] min-w-[44px] px-2 relative',
        'transition-colors duration-200',
        primary ? 'text-primary' : isActive ? 'text-foreground' : 'text-muted-foreground',
        !primary && 'hover:text-foreground'
      )}
    >
      <div className="relative">
        <CurrentIcon
          className={cn('transition-all duration-200', primary ? 'h-7 w-7' : 'h-6 w-6')}
        />
        {badge !== undefined && badge !== 0 && badge !== false && (
          <div
            className={cn(
              'absolute -top-1 -right-1 min-w-[16px] h-4 px-1',
              'bg-destructive text-destructive-foreground',
              'rounded-full text-[10px] font-bold',
              'flex items-center justify-center',
              'animate-in fade-in zoom-in duration-200'
            )}
          >
            {typeof badge === 'number' ? (badge > 99 ? '99+' : badge) : ''}
          </div>
        )}
      </div>
      <span className={cn('text-xs mt-1 font-medium', primary && 'font-semibold')}>{label}</span>
    </div>
  );

  if (href) {
    return (
      <Link to={href} className="flex-1">
        {content}
      </Link>
    );
  }

  return (
    <button onClick={onClick} className="flex-1">
      {content}
    </button>
  );
}

interface MobileBottomNavProps {
  onAddPlantClick: () => void;
}

export function MobileBottomNav({ onAddPlantClick }: MobileBottomNavProps) {
  const location = useLocation();
  const currentPath = location.pathname;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-30 bg-background border-t border-border safe-area-bottom">
      <nav className="flex items-center justify-around">
        <NavItem
          icon={HomeIcon}
          activeIcon={HomeIconSolid}
          label="Home"
          href="/dashboard"
          isActive={currentPath === '/dashboard'}
        />
        <NavItem
          icon={Square3Stack3DIcon}
          activeIcon={Square3Stack3DIconSolid}
          label="Plants"
          href="/plants"
          isActive={currentPath === '/plants'}
        />
        <NavItem
          icon={SparklesIcon}
          activeIcon={SparklesIconSolid}
          label="Seeds"
          href="/seeds"
          isActive={currentPath === '/seeds'}
        />
        <NavItem
          icon={PlusIcon}
          activeIcon={PlusIconSolid}
          label="Add"
          onClick={onAddPlantClick}
          primary
        />
        <NavItem
          icon={PrinterIcon}
          activeIcon={PrinterIconSolid}
          label="Labels"
          href="/labels"
          isActive={currentPath === '/labels'}
        />
        <NavItem
          icon={BuildingStorefrontIcon}
          activeIcon={BuildingStorefrontIconSolid}
          label="Sources"
          href="/sources"
          isActive={currentPath === '/sources'}
        />
      </nav>
    </div>
  );
}
